import { zodResolver } from '@hookform/resolvers/zod'
import {
	type ApplicationManagerDetail,
	applicationCorrectDirect
} from '@repo/sdk/gen'
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert'
import { Button } from '@repo/ui/button'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { TranscriptionGroups } from '../../-components/transcription-groups'
import {
	changesBetween,
	FIELD_LABEL,
	formatTranscriptionValue,
	placesAreSet,
	type TranscriptionForm,
	transcriptionFormSchema,
	transcriptionIssueMessage,
	transcriptionOf
} from '../../-lib/transcription'
import { applicationDetailQueryOptions } from '../../-query/query-options'

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/domande/$submissionId/correggi/'
)({
	// The loader reads a student's personal data and logs the access, as the
	// detail does: only opening the page runs it.
	preload: false,
	loader: async ({ context, params }) => {
		try {
			await context.queryClient.ensureQueryData(
				applicationDetailQueryOptions(
					params.residenceId,
					params.submissionId
				)
			)
		} catch (error) {
			if (
				typeof error === 'object' &&
				error !== null &&
				'code' in error &&
				(error.code === 'application_submission_not_found' ||
					error.code === 'residence_not_found')
			) {
				throw notFound()
			}
			throw error
		}
	},
	staticData: {
		breadcrumb: 'admin-applications:correction.page_title'
	},
	component: CorrectionPage
})

/**
 * "Correggi trascrizione" (FFD 2.4, errori comuni): the fields of a direct
 * application as they are now, then the differences before they are saved.
 * Only a valid direct application is corrected, and never its tax code.
 */
function CorrectionPage() {
	const { t } = useTranslation()
	const { residenceId, submissionId } = Route.useParams()
	const { data: application } = useSuspenseQuery(
		applicationDetailQueryOptions(residenceId, submissionId)
	)

	if (
		application.origin !== 'direct_pec' ||
		application.state !== 'submitted'
	) {
		return (
			<Alert className='max-w-2xl'>
				<AlertTitle>
					{t('admin-applications:correction.not_allowed_title')}
				</AlertTitle>
				<AlertDescription>
					{t('admin-applications:correction.not_allowed_description')}
				</AlertDescription>
			</Alert>
		)
	}
	return (
		<CorrectionForm application={application} residenceId={residenceId} />
	)
}

function CorrectionForm({
	residenceId,
	application
}: {
	residenceId: string
	application: ApplicationManagerDetail
}) {
	const { t, i18n } = useTranslation()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const [current] = useState(() => transcriptionOf(application.data))
	const [reviewing, setReviewing] = useState(false)
	const form = useForm<TranscriptionForm>({
		resolver: zodResolver(transcriptionFormSchema, {
			error: transcriptionIssueMessage
		}),
		defaultValues: {
			transcription: current,
			places: {
				birth: application.data.student.birthPlace,
				residence: application.data.student.residencePlace
			}
		},
		mode: 'onTouched'
	})
	const changes = reviewing
		? changesBetween(current, form.getValues('transcription'))
		: []

	const correct = useMutation({
		mutationFn: () =>
			applicationCorrectDirect({
				body: {
					data: {
						residenceId,
						submissionId: application.id,
						changes: changes.map(({ field, value }) => ({
							field,
							value
						}))
					}
				}
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.applications.all()
			})
			toast.success(t('admin-applications:correction.saved'))
			await navigate({
				to: '/bando/$residenceId/domande/$submissionId',
				params: { residenceId, submissionId: application.id }
			})
		},
		onError: (error) => toast.error(formatError(error))
	})

	async function review() {
		const ok = await form.trigger('transcription')
		if (!ok) {
			toast.error(t('admin-applications:intake.fix_fields'))
			return
		}
		const values = form.getValues('transcription')
		if (!placesAreSet(values)) {
			toast.error(t('admin-applications:intake.places_required'))
			return
		}
		if (changesBetween(current, values).length === 0) {
			toast.error(t('admin-applications:correction.no_changes'))
			return
		}
		setReviewing(true)
	}

	return (
		<FormProvider {...form}>
			<div className='flex min-w-0 flex-col gap-6'>
				<header className='flex flex-col gap-1'>
					<h1 className='font-semibold text-2xl'>
						{t('admin-applications:correction.page_title')}
					</h1>
					<p className='text-muted-foreground text-sm'>
						{t('admin-applications:correction.description')}
					</p>
				</header>
				{reviewing ? (
					<section className='flex flex-col gap-3'>
						<h2 className='font-semibold'>
							{t('admin-applications:correction.review_title')}
						</h2>
						<div className='overflow-x-auto rounded-lg border'>
							<table className='w-full text-sm'>
								<thead className='bg-muted/40 text-left'>
									<tr>
										<th className='px-4 py-2 font-medium'>
											{t(
												'admin-applications:correction.field'
											)}
										</th>
										<th className='px-4 py-2 font-medium'>
											{t(
												'admin-applications:correction.before'
											)}
										</th>
										<th className='px-4 py-2 font-medium'>
											{t(
												'admin-applications:correction.after'
											)}
										</th>
									</tr>
								</thead>
								<tbody className='divide-y divide-border'>
									{changes.map((change) => (
										<tr key={change.field}>
											<td className='px-4 py-2'>
												{t(FIELD_LABEL[change.field])}
											</td>
											<td className='px-4 py-2 text-muted-foreground'>
												{formatTranscriptionValue(
													change.field,
													change.before,
													t,
													i18n.language
												)}
											</td>
											<td className='px-4 py-2 font-medium'>
												{formatTranscriptionValue(
													change.field,
													change.value,
													t,
													i18n.language
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</section>
				) : (
					<TranscriptionGroups lockTaxCode />
				)}
				<div className='flex flex-wrap justify-between gap-3 border-t pt-6'>
					{reviewing ? (
						<>
							<Button
								onClick={() => setReviewing(false)}
								type='button'
								variant='outline'
							>
								{t('admin-applications:correction.back')}
							</Button>
							<Button
								disabled={correct.isPending}
								onClick={() => correct.mutate()}
								type='button'
							>
								{correct.isPending && <Spinner />}
								{t('admin-applications:correction.save')}
							</Button>
						</>
					) : (
						<Button
							className='ml-auto'
							onClick={review}
							type='button'
						>
							{t('admin-applications:correction.review')}
						</Button>
					)}
				</div>
			</div>
		</FormProvider>
	)
}
